import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import SEO, { collectionPageJsonLd, breadcrumbJsonLd } from '../components/SEO';
import { thermalCategories, thermalProductsByCategory, thermalProductPath } from '../data/thermal';
import ThermalCamerasCompareTable from '../components/ThermalCamerasCompareTable';

const ThermalHubPage = ({ category, onRequestDemo, onRequestQuote }) => {
  const cat = thermalCategories[category];
  const products = thermalProductsByCategory(category);
  const canonical = `https://ctrl.aero${cat.path}`;
  const other = Object.values(thermalCategories).find((c) => c.slug !== category);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${cat.name} — ctrl.aero`}
        description={cat.metaDescription || cat.intro}
        canonical={canonical}
        image={products[0]?.images[0]}
        jsonLd={[
          collectionPageJsonLd({
            name: cat.name,
            description: cat.intro,
            url: canonical,
            items: products.map((p) => ({ name: p.name, url: `https://ctrl.aero${thermalProductPath(p)}` })),
          }),
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://ctrl.aero/' },
            { name: cat.name, url: canonical },
          ]),
        ]}
      />
      <Header onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />

      <section className="relative isolate overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 pt-24 pb-14 sm:pt-28 sm:pb-16">
        <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-blue-500/20 blur-3xl" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link to="/" className="inline-flex items-center text-white/70 hover:text-white text-sm mb-6 transition-colors" data-testid={`thermal-hub-back-home-${category}`}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
          </Link>
          <p className="text-orange-400 text-xs sm:text-sm font-semibold uppercase tracking-wide mb-2">{cat.name}</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl">{cat.headline}</h1>
          <p className="mt-5 text-base sm:text-lg text-blue-100/85 leading-relaxed max-w-prose">{cat.intro}</p>
          <div className="cta-group cta-group-compact mt-8 max-w-md">
            <Button onClick={onRequestDemo} size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-[15px] font-semibold" data-testid={`thermal-hub-demo-${category}`}>Request Demo</Button>
            <Button onClick={onRequestQuote} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white text-sm sm:text-[15px] font-semibold" data-testid={`thermal-hub-quote-${category}`}>Request Quote</Button>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <Link
                key={p.slug}
                to={thermalProductPath(p)}
                className="group flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-blue-300 transition-all overflow-hidden"
                data-testid={`thermal-card-${p.slug}`}
              >
                <div className="aspect-[4/3] bg-gradient-to-br from-slate-100 to-blue-50 flex items-center justify-center p-6">
                  <img src={p.images[0]} alt={p.name} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                </div>
                <div className="p-5 flex flex-col flex-grow">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-600 mb-1">{p.models}</p>
                  <h2 className="text-lg font-bold text-[#1e2d3b] leading-snug">{p.name}</h2>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-grow">{p.tagline}</p>
                  <ul className="mt-4 flex flex-wrap gap-1.5">
                    {p.highlights.map((h) => (
                      <li key={h} className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700">{h}</li>
                    ))}
                  </ul>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 group-hover:text-blue-700">
                    View details <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-slate-600">
            Also see{' '}
            <Link to={other.path} className="font-semibold text-blue-600 hover:underline" data-testid={`thermal-hub-other-${category}`}>{other.name}</Link>
            {' '}and{' '}
            <Link to="/products/fotric" className="font-semibold text-blue-600 hover:underline">Acoustic Cameras</Link>.
          </p>
        </div>
      </section>
      {category === 'thermal-cameras' && <ThermalCamerasCompareTable />}
      <Footer />
    </div>
  );
};

export default ThermalHubPage;
